import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { take } from 'rxjs/operators'
import { OpenLotRow } from '../models/open-lot-row.model'
import { SpecLotContext } from '../models/spec-Lot-context.model'

/**
 * @internal
 */
export interface UpdatedSpecLotMatch {
  updateType: string
  lotEntryTranId: string
  lotEntryLegacyOrigId: number
  units: number
  note: string | undefined
  inventoryType: string
}

/**
 * @internal
 */
export interface UpdatedSpecLotMatches {
  accountId: number
  securityId: number
  exitTranId: string
  exitLegacyOrigId: number
  updatedMatches: UpdatedSpecLotMatch[]
  note: string
}

/**
 * @internal
 */
export interface startingSpecLotMatchesData {
  hasSpecLotMatch: boolean
  units: number
}

const SPEC_LOT_MATCHES_URL = `helios-spec-lot-matches`
/**
 * @internal
 */
export const CONTEXT_URL = `${SPEC_LOT_MATCHES_URL}/context/tran/`

/**
 * @internal
 */
export const OPEN_LOTS_URL = `${SPEC_LOT_MATCHES_URL}/openLots/`

/**
 * @internal
 */
export const UPDATE_MATCHES_URL = `${SPEC_LOT_MATCHES_URL}/actions/specLotMatches`

@Injectable({
  providedIn: 'root',
})
export class SpecLotMatchesService {
  constructor(private readonly http: HttpClient) {}
  async updateSpecLotMatches(
    accountId: number,
    securityId: number,
    openLotRows: OpenLotRow[],
    exitTranId: string,
    exitLegacyOrigId: number,
    startingSpecLotMatchDataMap: Map<string, startingSpecLotMatchesData>,
  ): Promise<void> {
    const specLotMatches: UpdatedSpecLotMatches = {
      accountId: accountId,
      securityId: securityId,
      exitTranId: exitTranId,
      exitLegacyOrigId: exitLegacyOrigId,
      note: '',
      updatedMatches: openLotRows
        .map(row => ({
          updateType: this.getUpdateType(
            startingSpecLotMatchDataMap.get(row.lotEntryTranId)
              ?.hasSpecLotMatch ?? false,
            row.hasSpecLotMatch,
            startingSpecLotMatchDataMap.get(row.lotEntryTranId)?.units ?? 0,
            row.units,
          ),
          lotEntryTranId: row.lotEntryTranId,
          lotEntryLegacyOrigId: row.lotEntryLegacyOrigId,
          units: row.units,
          note: row.note,
          inventoryType: row.inventoryType,
        }))
        .filter(row => row.updateType != ''),
    }

    await this.http
      .post(UPDATE_MATCHES_URL, specLotMatches)
      .pipe(take(1))
      .toPromise()
  }

  getOpenLotRows(exitTranId: string): Promise<OpenLotRow[]> {
    return this.http
      .get<OpenLotRow[]>(OPEN_LOTS_URL + exitTranId)
      .pipe(take(1))
      .toPromise()
  }

  getContext(exitTranId: string): Promise<SpecLotContext> {
    return this.http
      .get<SpecLotContext>(CONTEXT_URL + exitTranId)
      .pipe(take(1))
      .toPromise()
  }

  getUpdateType(
    startingHasSpecLotMatch: boolean,
    currentHasSpecLotMatch: boolean,
    startingUnits: number,
    currentUnits: number,
  ): string {
    if (!startingHasSpecLotMatch && currentHasSpecLotMatch) {
      return 'ADD'
    }

    if (!startingHasSpecLotMatch) {
      return ''
    }

    if (!currentHasSpecLotMatch) {
      return 'DELETE'
    }

    if (startingUnits == currentUnits) {
      return 'KEEP'
    }

    return 'EDIT'
  }
}
